import {z} from 'zod';

import {
    ReviewsStateType,
    ReviewsStateUrlQueryNameEnum,
} from '../../../page/main/reviews/reviews-state/reviews-state-type';
import {fetchAndDeserialize} from '../../../util/api-adapter';
import {FetchMethodEnum, fetchNoContent} from '../../../util/fetch';
import {QueryMapType} from '../../../util/type';
import {objectToUrlParameters} from '../../../util/url';
import {getCsrfHeaders, mainApiHeaders, rootApiUrl} from '../../api/api-const';
import {ConcordanceAiPresetEnum} from '../../reivew-analysis/review-analysis-ai-chat/review-analysis-ai-message-type';
import {reviewsStateToUrlObject} from '../reviews-helper';

// ReviewFilter rejects filter_key combined with brand/company/city/group id filters,
// so the two scoping modes must be mutually exclusive.
const FILTER_KEY_CONFLICTING_KEYS = new Set(['brand_ids', 'company_ids', 'city_ids', 'company_group_ids']);

// Same filter params the reviews list sends to /v4/reviews/, so RocketGPT analyses
// exactly the current selection.
function getReviewsAiChatFilterParameters(reviewsState: ReviewsStateType): QueryMapType {
    const parameters = reviewsStateToUrlObject(reviewsState);

    // Explicit company scoping is sent as-is (matches the reviews list); no filter_key.
    if (parameters[ReviewsStateUrlQueryNameEnum.companyIds] || !reviewsState.filterId) {
        return parameters;
    }

    // Fall back to the saved filter: filter_key already encodes the company/brand scope,
    // so drop the conflicting id-filters to satisfy ReviewFilter.
    const scopedParameters = Object.fromEntries(
        Object.entries(parameters).filter(([key]) => !FILTER_KEY_CONFLICTING_KEYS.has(key))
    );

    return {...scopedParameters, filter_key: reviewsState.filterId};
}

type ReviewsAiMessagePayloadType = {
    reviewsState: ReviewsStateType;
    channelId: string;
    requestId: string;
    message: string;
    presetKey?: ConcordanceAiPresetEnum;
};

export function sendMessageToReviewsAiChat(payload: ReviewsAiMessagePayloadType): Promise<void> {
    const url = `${rootApiUrl}/cp/reviews/rocket_gpt/`;

    return fetchNoContent(url, {
        method: FetchMethodEnum.post,
        headers: {
            ...mainApiHeaders,
            ...getCsrfHeaders(),
        },
        body: JSON.stringify({
            ...getReviewsAiChatFilterParameters(payload.reviewsState),
            channel_id: payload.channelId,
            request_id: payload.requestId,
            ...(payload.presetKey ? {preset_key: payload.presetKey} : {}),
            ...(payload.message ? {message: payload.message} : {}),
        }),
    });
}

const reviewsAiChatEligibilitySchema = z.object({
    reviewsCount: z.number(),
});

export type ReviewsAiChatEligibilityType = z.infer<typeof reviewsAiChatEligibilitySchema>;

export function getReviewsAiChatEligibilityUrl(reviewsState: ReviewsStateType): string {
    return `/cp/reviews/rocket_gpt/eligibility/?${objectToUrlParameters(
        getReviewsAiChatFilterParameters(reviewsState)
    )}`;
}

export function fetchReviewsAiChatEligibility(reviewsState: ReviewsStateType): Promise<ReviewsAiChatEligibilityType> {
    return fetchAndDeserialize<ReviewsAiChatEligibilityType>(
        getReviewsAiChatEligibilityUrl(reviewsState),
        reviewsAiChatEligibilitySchema
    );
}
